import axios from 'axios'

import {
    AgreementExportType,
    COP_API_BASE_URL,
    UPDATE_SITE_CONTACTS_ENDPOINT,
    getSiteDetailsUrl,
    getSiteExportDetailsUrl,
    SITE_TREE_VIEW_ENDPOINT,
} from '../../../constants/service-endpoints'
import { get, post } from '../../../services/http.service'
import { buildCopHeaders, getSilentToken } from '../../../services/tokenUtils'

const SITE_EXPORT_FILE_NAME: Record<AgreementExportType, string> = {
    Excel: 'site-details.xlsx',
    PDF: 'site-details.pdf',
}

export type SiteContactData = {
    title?: string | null
    salutation?: string | null
    firstName?: string | null
    lastName?: string | null
    email?: string | null
    phone?: string | null
    role?: string | null
}

export type SiteDetailsResponse = {
    id?: string
    name?: string
    address?: string | null
    city?: string | null
    country?: string | null
    customerId?: string
    customerSiteName?: string | null
    industryUsage?: string | null
    owner?: string | null
    operator?: string | null
    latitude?: number | null
    longitude?: number | null
    operationalStatus?: string | null
    assetsCount?: number | null
    primaryContact?: SiteContactData | null
    secondaryContact?: SiteContactData | null
    [key: string]: unknown
}

export type AssetTreeNode = {
    id: string
    name: string
    type: string
    parentId?: string | null
    children?: AssetTreeNode[]
    level?: number
    hasChildren?: boolean
    metadata?: Record<string, unknown>
}

export type SiteAsset = {
    assetId: string
    hasDetails: boolean
    siteId: string
    productCategoryName: string
    productCategoryId: number
    operationalStatus: string
    serialNumber: string
    description: string
    assets: SiteAsset[]
}

export type SiteAreaContainer = {
    containerName: string
    city: string
    country: string
    treeViewItemType: string
    assets: SiteAsset[]
}

export type SiteTreeViewResponse = {
    siteId: string
    siteName: string
    treeViewItemType: string
    siteAreas: SiteAreaContainer[]
    decommissionedAssetsContainers: SiteAreaContainer[]
    spareAssetsContainers: SiteAreaContainer[]
    assets: SiteAsset[]
}

export async function fetchSiteDetails(siteId: string): Promise<SiteDetailsResponse | null> {
    const url = `${COP_API_BASE_URL}${getSiteDetailsUrl(siteId)}`
    const data = await get<SiteDetailsResponse>(url)
    return data ?? null
}

export type UpdateContactsPayload = {
    primaryContact?: SiteContactData
    secondaryContact?: SiteContactData
}

export async function updateSiteContacts(siteId: string, payload: UpdateContactsPayload): Promise<void> {
    await post(`${COP_API_BASE_URL}${UPDATE_SITE_CONTACTS_ENDPOINT}`, { payload: { ...payload, siteId } })
}

export async function exportSiteDetails(siteId: string, exportType: AgreementExportType): Promise<void> {
    const url = `${COP_API_BASE_URL}${getSiteExportDetailsUrl(siteId, exportType)}`
    const token = getSilentToken()
    const headers = buildCopHeaders(token)
    const response = await axios.get<Blob>(url, { headers, responseType: 'blob' })
    const blob = new Blob([response.data], { type: response.headers['content-type'] as string })
    const objectUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = objectUrl
    link.download = SITE_EXPORT_FILE_NAME[exportType]
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(objectUrl)
}

export async function fetchSiteTreeView(siteId: string): Promise<SiteTreeViewResponse | null> {
    const url = `${COP_API_BASE_URL}${SITE_TREE_VIEW_ENDPOINT}(siteId='${siteId}')`
    const data = await get<SiteTreeViewResponse>(url)
    return data ?? null
}

// Resolves the current site ID from query params (?id / ?siteId), or the last non-.html path segment.
export function getSiteIdFromUrl(): string | null {
    const params = new URLSearchParams(window.location.search)
    const queryId = params.get('id') ?? params.get('siteId')
    if (queryId) return queryId
    const segments = window.location.pathname.split('/').filter(Boolean)
    const last = segments.at(-1)
    return last && !last.endsWith('.html') ? last : null
}